import type { CreateLumappsChatAgentConfig } from '@lumapps/agent-chat/api';
import type { AiAgentMessage } from '@lumapps/agent-chat/types';
import type { AgentSourceReference } from '@lumapps/agent-hub-sources/types';
import type { MicroAppResponse } from '@lumapps/ask-ai-rules/types';
import type { ServerListResponse } from '@lumapps/base-api';
import type { GroupReference } from '@lumapps/groups/types';
import type { InputVariableDefinition } from '@lumapps/micro-apps-builder/types';
import type { createEntityManagementRoutes } from '@lumapps/router';
import type { TranslatableObject } from '@lumapps/translations';
import type { UserReference } from '@lumapps/user/types';

// ============================================================================
// Agent Hub Admin Types
// ============================================================================

/** Types returned by the createEntityManagementRoutes function */
type CreateEntityManagementRoutes = ReturnType<typeof createEntityManagementRoutes>;

/** Base configuration that will be common to all agent hub pages that should also work with ask */
export interface AgentHubAdminCompatibilityContextOptions {
    /** The translation key of the label to display in the admin header */
    parentLabelKey: string;
    /** Routes for each admin pages. Should come from "createEntityManagementRoutes" util */
    adminRoutes: Pick<CreateEntityManagementRoutes, 'baseRoute' | 'createRoute' | 'editRoute' | 'getEditRoute'>;
}

// ============================================================================
// Agent Tool Types
// ============================================================================

/**
 * The type of a tool that can be used by an agent.
 * Native indicates the tool is implemented natively in the executor,
 * microapp indicates that the tool is implemented via a microapp.
 */
export enum AgentToolType {
    /** The tool is implemented natively in the executor. */
    NATIVE = 'native',
    /** The tool is implemented via a microapp. */
    MICROAPP = 'microapp',
}

/**
 * Which software component is responsible for running the tool.
 */
export enum AgentExecutorType {
    /** The frontend is responsible for running the tool. */
    FRONTEND = 'frontend',
    /** The backend is responsible for running the tool. */
    BACKEND = 'backend',
}

/**
 * A tool that can be used by an agent.
 */
export type AgentTool = {
    /** The tool identifier. */
    id: string;
    /** The tool name. */
    name: TranslatableObject;
    /** The tool type. */
    type: AgentToolType;
    /** Which software component is responsible for running the tool. */
    executor: AgentExecutorType;
};

/**
 * Response for the list of agent tools.
 */
export type AgentToolListResponse = ServerListResponse<AgentTool>;

// ============================================================================
// Agent Core Types (moved from agent-library)
// ============================================================================

/**
 * The type of an agent.
 */
export enum AgentType {
    /** A RAG agent. */
    RAG = 'rag',
    /** A react stateless agent. */
    REACT_STATELESS = 'react_stateless',
    /** A static answer agent. */
    STATIC_ANSWER = 'static_answer',
    /** An external agent. */
    EXTERNAL = 'external',
}

/**
 * Status of the agent ("deleted" is not listed because "deleted" entities are never shown by the API).
 */
export enum AgentStatus {
    /** The agent is live. */
    LIVE = 'live',
    /** The agent is a draft. */
    DRAFT = 'draft',
}

/**
 * The base properties of an agent.
 */
export interface AgentBase {
    /** The agent identifier. */
    id: string;
    /** The agent name. */
    name: Partial<TranslatableObject>;
}

export type ChatAgentBase = AgentBase & {
    /** Messages to initiate the agent with */
    initialMessages: AiAgentMessage[];
};

/** Reference for micro app type entities */
export type AgentMicroAppReference = {
    id: string;
    name: TranslatableObject;
    description?: TranslatableObject;
    inputVariableDefinitions: InputVariableDefinition[];
};

/** References given by the agent to use in tools */
export type AgentReferencedEntities = {
    microapp?: AgentMicroAppReference[];
};

/**
 * The properties that are common to all agent types.
 */
export interface AgentCommonProperties extends AgentBase {
    /** The agent status. */
    status: AgentStatus;
    /** The agent type. */
    type: AgentType;
    /** The agent description. */
    description: TranslatableObject;
    /** The agent icon name. Must be the name of a material-design icon. */
    iconName: string;
    /** The background color of the agent icon, in rgba css format. */
    iconBackgroundColor: string;
    /** The groups that can see the agent. */
    visibleByGroups: GroupReference[];
    /** Whether the current user can update the agent. */
    canUpdate: boolean;
    /** Whether the current user can delete the agent. */
    canDelete: boolean;
    /** The time and date when the agent was created. It is absent for built-in agents. */
    createdAt?: string;
    /** The time and date when the agent was last updated. It is absent for built-in agents. */
    updatedAt?: string;
    /** The user who created the agent. It is absent for built-in agents. */
    createdBy?: UserReference;
    /** The user who last updated the agent. It is absent for built-in agents. */
    updatedBy?: UserReference;
    /** The tools that the agent can use. */
    tools?: AgentTool[];
    /** Up to `MAX_CONVERSATION_STARTERS` suggestions to be displayed on conversation initiation */
    conversationStarters?: TranslatableObject[];
    /** Reference to entities needed for tools to work properly */
    referencedEntities?: AgentReferencedEntities;
}

/**
 * A RAG (Retrieval Augmented Generation) agent.
 */
export interface RagAgent extends AgentCommonProperties {
    /** The agent type. */
    type: AgentType.RAG;
    /** The agent prompt, instructing what it should do. */
    prompt: string;
    /** The sources on which the agent relies to fetch documents. */
    sources: AgentSourceReference[];
}

/**
 * Base properties for a static answer agent.
 */
type StaticAnswerAgentBase = AgentCommonProperties & {
    /** The agent type. */
    type: AgentType.STATIC_ANSWER;
    /** The answer with which the agent always replies (LocalizedString). */
    staticAnswer: TranslatableObject;
};

/**
 * A static answer agent that displays a micro app.
 */
type StaticAnswerAgentWithMicroApp = StaticAnswerAgentBase & {
    /** ID of the micro app displayed by this agent. */
    displayedMicroAppId: string;
    /** The details of the microapp displayed by the agent. */
    displayedMicroApp: MicroAppResponse;
    displayedUserId?: never;
    displayedUser?: never;
};

/**
 * A static answer agent that displays a user.
 */
type StaticAnswerAgentWithUser = StaticAnswerAgentBase & {
    /** ID of the user displayed by this agent. */
    displayedUserId: string;
    /** The details of the user displayed by the agent. */
    displayedUser: UserReference;
    displayedMicroAppId?: never;
    displayedMicroApp?: never;
};

/**
 * A static answer agent that displays with no actionable items.
 */
type StaticAnswerAgentWithoutActionableItem = StaticAnswerAgentBase & {
    displayedMicroAppId?: never;
    displayedMicroApp?: never;
    displayedUserId?: never;
    displayedUser?: never;
};

/**
 * An agent that always replies with the same static answer.
 *
 * This agent can optionally display either a micro app or a user (but not both).
 */
export type StaticAnswerAgent =
    | StaticAnswerAgentWithMicroApp
    | StaticAnswerAgentWithUser
    | StaticAnswerAgentWithoutActionableItem;

/**
 * A stateless agent that can use tools.
 */
export interface ReactStatelessAgent extends AgentCommonProperties {
    /** The agent type. */
    type: AgentType.REACT_STATELESS;
    /** The agent prompt, instructing what the agent should do. */
    prompt: string;
    /** The sources on which the agent relies to fetch documents (knowledge). */
    sources?: AgentSourceReference[];
}

/**
 * An external agent that redirects users to an external URL.
 */
export interface ExternalAgent extends AgentCommonProperties {
    /** The agent type. */
    type: AgentType.EXTERNAL;
    /** The external URL to redirect users to. */
    redirectUrl: string;
}

/**
 * An agent.
 */
export type Agent = RagAgent | StaticAnswerAgent | ReactStatelessAgent | ExternalAgent;

/** Object received when listing available AI Agents: GET call to /agents */
export type AiAgentsListDTO = ServerListResponse<Agent>;

// ============================================================================
// Agent Input Types (for Create/Update operations)
// ============================================================================

/**
 * Base input fields for creating/updating agents.
 * Derived from AgentCommonProperties by removing read-only fields.
 */
export type AgentInputBase = Pick<
    AgentCommonProperties,
    'name' | 'description' | 'iconName' | 'iconBackgroundColor' | 'status' | 'conversationStarters'
> & {
    /** The groups that can see the agent (input format). */
    visibleByGroups?: { id: string }[];
};

/**
 * Input for creating/updating a RAG agent.
 */
export type RagAgentInput = AgentInputBase & Pick<RagAgent, 'type' | 'prompt' | 'sources'>;

/**
 * Input for creating/updating a static answer agent.
 */
export type StaticAnswerAgentInput = AgentInputBase &
    Pick<StaticAnswerAgentBase, 'type' | 'staticAnswer'> & {
        /** ID of the micro app displayed by this agent. Mutually exclusive with displayedUserId. */
        displayedMicroAppId?: string;
        /** ID of the user displayed by this agent. Mutually exclusive with displayedMicroAppId. */
        displayedUserId?: string;
    };

/**
 * Input for creating/updating a react stateless agent.
 */
export type ReactStatelessAgentInput = AgentInputBase &
    Pick<ReactStatelessAgent, 'type' | 'prompt' | 'sources'> & {
        /** The tools that the agent can use (input format). */
        tools: { id: string }[];
    };

/**
 * Input for creating/updating an external agent.
 */
export type ExternalAgentInput = AgentInputBase & Pick<ExternalAgent, 'type' | 'redirectUrl'>;

/**
 * Union type for agent input (used for both create and update operations).
 */
export type AgentInput = RagAgentInput | StaticAnswerAgentInput | ReactStatelessAgentInput | ExternalAgentInput;

// ============================================================================
// Agent Hub State Types
// ============================================================================

/** state of the redux store used for the chat drawer */
export interface AgentHubState {
    /** whether the ai chat is opened */
    isChatOpen: boolean;
    /**
     * Configuration used for the currently used agent
     * Will only be set once on agent initialization but will not update
     * during the agent's lifecycle.
     */
    selectedAgent: CreateLumappsChatAgentConfig;
    pendingAgentChange?: CreateLumappsChatAgentConfig;
}

/**
 * Parameters for listing agents.
 */
export interface ListAgentsParams {
    /** Filter by agent status. Can be a single status or array for multiple statuses. */
    status?: AgentStatus | AgentStatus[];
    /** Cursor for pagination. */
    cursor?: string;
    /** Maximum number of results to return. */
    maxResults?: number;
    /** Sort order (e.g., '-status' for descending status). */
    sortOrder?: string;
    /** Filter listed agents by type. Can be specified multiple times for OR filtering */
    type?: AgentType[];
    /** Search term for filtering agents. */
    search?: string;
    /** Field to search in (name, description, or name_or_description). */
    searchIn?: 'name' | 'description' | 'name_or_description';
    /** Filter by agent creators. Array of user IDs. */
    createdBy?: string[];
    /** Filter by agent sources. Array of source UUIDs. */
    sourceIds?: string[];
}
